import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { captureCouponFromUrl, captureReferralFromUrl } from './utils/couponReferral';
import LoginNotificationOverlay from './components/LoginNotificationOverlay';
import { useAuth } from './context/AuthContext';
import { DataCacheProvider } from './context/DataCacheContext';
import { SyncStatusProvider } from './context/SyncStatusContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Toaster, toast, useSonner } from 'sonner';
import { X } from 'lucide-react';
import ImpersonationBanner from './components/ImpersonationBanner';
import GlobalSyncBanner from './components/GlobalSyncBanner';
import ThemeToggle from './components/ThemeToggle';
import SupportChatWidget from './components/SupportChatWidget';
import Landing from './pages/Landing';
import AuthSuccess from './pages/AuthSuccess';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Settings from './pages/Settings';
import Expenses from './pages/Expenses';
import Analytics from './pages/Analytics';
import Profitability from './pages/Profitability';
// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConnectMeli from './pages/ConnectMeli';
import AdminPanel from './pages/AdminPanel';
import OnboardingLoader from './pages/OnboardingLoader';
import Publications from './pages/Publications';
import ProductResearch from './pages/ProductResearch';
import SelectPlan from './pages/SelectPlan';
import UpdatePayment from './pages/UpdatePayment';
import SubscriptionReturn from './pages/SubscriptionReturn';
import CreditsReturn from './pages/CreditsReturn';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Maintenance from './pages/Maintenance';
import RequireSubscription from './components/RequireSubscription';
import RequireConsultant from './components/RequireConsultant';
import Affiliates from './pages/Affiliates';
import Referrals from './pages/Referrals';

// REACT_APP_MAINTENANCE_MODE=true at build time takes every route except
// /privacy down to a maintenance page. Set at build/deploy time (CRA only
// embeds REACT_APP_* vars that exist when `yarn build` runs), so toggling
// this back off means rebuilding and redeploying with the var unset —
// there's no runtime switch.
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

// Redirects super_admin users to /admin unless they are actively impersonating
// a seller, and consultants (who can't use Selltrix) to their affiliate area.
const AppRoute = ({ element }) => {
  const { isAdmin, isImpersonating, isConsultant } = useAuth();
  if (isAdmin && !isImpersonating) return <Navigate to="/admin" replace />;
  if (isConsultant) return <Navigate to="/afiliados" replace />;
  return element;
};

// Small pill above the toast stack to dismiss every active toast at once.
// Only rendered while at least one toast is visible.
const ClearToastsButton = () => {
  const { toasts } = useSonner();
  if (toasts.length === 0) return null;

  return (
    <button
      type="button"
      onClick={() => toast.dismiss()}
      className="fixed top-2 right-4 z-[2147483647] flex items-center gap-1 rounded-full border border-border bg-background/90 px-2 py-0.5 text-xs text-muted-foreground shadow-sm backdrop-blur transition-colors hover:bg-muted"
      title="Cerrar todas las notificaciones"
    >
      <X className="h-3 w-3" />
      Limpiar avisos
    </button>
  );
};

// Renders inside ThemeProvider so it can read the active theme.
const GlobalChrome = () => {
  const { theme } = useTheme();
  return (
    <>
      {/* bottom-left, so the chat widget keeps sole ownership of bottom-right
          — the conventional corner for a support-chat launcher */}
      <div className="fixed bottom-5 left-5 z-[9999]">
        <ThemeToggle />
      </div>
      <ClearToastsButton />
      <Toaster
        position="top-right"
        theme={theme}
        offset={{ top: 44 }}
        mobileOffset={{ top: 44 }}
      />
      <SupportChatWidget />
      <LoginNotificationOverlay />
    </>
  );
};

function App() {
  // Runs on every full page load, regardless of which route a QR code (or
  // any campaign link) lands on — see utils/couponReferral.js.
  useEffect(() => {
    captureCouponFromUrl();
    captureReferralFromUrl();
  }, []);

  if (MAINTENANCE_MODE) {
    // ThemeProvider is still required here — BrandLogo (used by both
    // PrivacyPolicy and Maintenance) calls useTheme() unconditionally.
    return (
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <SyncStatusProvider>
          <DataCacheProvider>
            <BrowserRouter>
              <ImpersonationBanner />
              <GlobalSyncBanner />
              <Routes>
                <Route path="/" element={<Landing />} />
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/select-plan" element={<SelectPlan />} />
                <Route path="/update-payment" element={<UpdatePayment />} />
                <Route path="/subscription/return" element={<SubscriptionReturn />} />
                <Route path="/credits/return" element={<CreditsReturn />} />
                <Route path="/connect-meli" element={<RequireSubscription><ConnectMeli /></RequireSubscription>} />
                <Route path="/auth/success" element={<AuthSuccess />} />
                <Route path="/onboarding" element={<OnboardingLoader />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                {/* Admin Route */}
                <Route path="/admin" element={<AdminPanel />} />
                {/* Consultant affiliate area — the only page consultant accounts can use */}
                <Route path="/afiliados" element={<RequireConsultant><Affiliates /></RequireConsultant>} />
                {/* App Routes — admin users are redirected to /admin unless impersonating; all require an active subscription */}
                <Route path="/dashboard" element={<RequireSubscription><AppRoute element={<Dashboard />} /></RequireSubscription>} />
                <Route path="/inventory" element={<RequireSubscription><AppRoute element={<Inventory />} /></RequireSubscription>} />
                <Route path="/settings" element={<RequireSubscription><AppRoute element={<Settings />} /></RequireSubscription>} />
                <Route path="/referidos" element={<RequireSubscription><AppRoute element={<Referrals />} /></RequireSubscription>} />
                <Route path="/cost" element={<RequireSubscription><AppRoute element={<Expenses />} /></RequireSubscription>} />
                <Route path="/analytics" element={<RequireSubscription><AppRoute element={<Analytics />} /></RequireSubscription>} />
                <Route path="/profitability" element={<RequireSubscription><AppRoute element={<Profitability />} /></RequireSubscription>} />
                <Route path="/publications" element={<RequireSubscription><AppRoute element={<Publications />} /></RequireSubscription>} />
                <Route path="/research" element={<RequireSubscription><AppRoute element={<ProductResearch />} /></RequireSubscription>} />
                {/* Redirect old SEO route to Publications with SEO tab */}
                <Route path="/seo-titles" element={<Navigate to="/publications?tab=seo" replace />} />
              </Routes>
            </BrowserRouter>
            <GlobalChrome />
          </DataCacheProvider>
        </SyncStatusProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
